import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import CssBaseline from '@material-ui/core/CssBaseline';
import getTheme from 'theme';
import palettes from 'common/paletteTypes';
import AOS from 'aos';
import { TOTAL_TOOLS_LOGO_SRC } from './views/landingPages/TNOMediaKitEnProduccionConMem/brandAssets';
import { getStaticProposalBySlug } from './data/staticProposals';

const SACRED_CODES_TEXT = [
  '715',
  '726',
  '8157',
  '71588',
  '520',
].join(' ');

/** Imagen por defecto en previews (WhatsApp, etc.) cuando la ruta no define otra. */
const DEFAULT_OG_IMAGE = '/images/logos/logo_tnonetwork_black.png';
const SHOWS_CDN = 'https://files.tnonetwork.com';
const showLogoOgImage = (slug) => `${SHOWS_CDN}/shows/logo/${slug}.png`;

/**
 * Devuelve título y meta description según hostname y pathname (para preview y SEO).
 * Así el documento y las meta og cambian por dominio/ruta (ej. academy, taller, ladosispositiva).
 */
function getPageMeta(hostname = '', pathname = '') {
  const path = (pathname || '').toLowerCase();
  const host = (hostname || '').toLowerCase();
  const pathTrim = path.replace(/\/+$/, '') || '/';
  if (pathTrim.startsWith('/ticket/')) {
    return {
      title: 'TNO — Credencial digital',
      description:
        'Tu acceso oficial al evento TNO Network. Muestra este código en la entrada.',
    };
  }
  if (pathTrim === '/admin/scanner') {
    return {
      title: 'TNO — Check-in (interno)',
      description: 'Herramienta de escaneo de credenciales para el equipo en puerta.',
    };
  }
  if (
    (pathTrim === '/' || pathTrim === '') &&
    (host === 'tnomedialab.com' ||
      host === 'www.tnomedialab.com' ||
      host === 'server1.tnomedialab.com' ||
      host === '162.0.214.75')
  ) {
    return {
      title: 'TNO Media Lab | Producción, tecnología y comunidad TNO',
      description:
        'TNO Media Lab: productora audiovisual, brazo tecnológico cherryDev, webs/tiendas y comunidad de creadores de TNO Network.',
      ogImage: '/images/tno-media-lab/logo.svg?v=2',
    };
  }
  if (host === 'noticiastno.tnonetwork.com' || pathTrim === '/noticias') {
    return {
      title: 'Noticias TNO | Anuncia con nosotros',
      description:
        'Noticias TNO: La evolución de la información en vivo con la autoridad de Sol Vargas. Patrocinios, rotativas y opciones de comercialización para marcas que buscan asociarse con el prestigio periodístico.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (
    host === 'medialab.tnonetwork.com' ||
    pathTrim === '/medialab' ||
    pathTrim === '/creadores' ||
    pathTrim === '/talentos'
  ) {
    return {
      title: 'TNO Media Lab | Laboratorio de contenidos',
      description:
        'Laboratorio de contenidos de TNO Network: tú llegas con el programa, el Lab produce, estrenamos en streaming en vivo y el show queda en on-demand, clips y tnonetwork.com.',
      ogImage: '/images/tno-media-lab/logo.svg?v=2',
    };
  }
  if (pathTrim === '/cherry-dev' || pathTrim === '/cherry-software' || pathTrim === '/codigo-lechuza') {
    return {
      title: 'cherryDev | Sistemas a medida Full Stack',
      description:
        'cherryDev: ingeniería Full Stack para software contable, e-commerce de alta capacidad, agendamiento y automatización interna. Diagnóstico técnico gratuito.',
      ogImage: '/images/cherry-software/logo.svg',
    };
  }
  if (pathTrim === '/cherry-web') {
    return {
      title: 'cherryDev | Web y Tienda Online en 7 días',
      description:
        'Convierte visitas en clientes con una web o tienda online profesional en menos de 7 días. Dominio y hosting gratis el primer año. Cotización por WhatsApp.',
      ogImage: '/images/cherry-software/logo.svg',
    };
  }
  if (pathTrim === '/cherry-web/anna-sanellia') {
    return {
      title: 'cherryDev × Anna Sanellia Silva | Propuesta web legal',
      description:
        'Propuesta cherryDev para Anna Sanellia Silva: web profesional, blog legal y agendamiento de citas. Tres planes desde $80 USD.',
      ogImage: '/images/logos/cherrydev.png',
    };
  }
  if (pathTrim === '/propuesta/farmafer') {
    return {
      title: 'cherryDev × Farmafer | Propuesta e-commerce',
      description:
        'Propuesta formal cherryDev para Farmafer: tienda online con catálogo, pedidos por WhatsApp, dominio y hosting. Inversión total $130 USD.',
      ogImage: '/images/logos/cherrydev.png',
    };
  }
  if (pathTrim === '/propuesta/guomei' || pathTrim === '/propuesta/guomei/publicidad') {
    return {
      title: 'cherryDev × Guomei Latam | Propuesta web y redes',
      description:
        'Propuesta cherryDev para Guomei Latam: plataforma e-commerce de viajes ($130) y paquetes mensuales de redes sociales. Registro de pagos, cotización WhatsApp y Ads aparte.',
      ogImage: '/images/logos/cherrydev.png',
    };
  }
  if (pathTrim === '/propuesta/nasiol' || pathTrim === '/propuesta/nasiol/publicidad') {
    return {
      title: 'cherryDev × Nasiol Venezuela | Catálogo online y redes',
      description:
        'Propuesta cherryDev para Nasiol Venezuela: catálogo web administrable ($160, dominio y hosting 1er año) y paquetes mensuales de redes. Automotriz, marítimo e industrial.',
      ogImage: '/images/logos/cherrydev.png',
    };
  }
  if (host === 'ladosispositiva.tnonetwork.com' || pathTrim === '/ladosispositiva' || pathTrim === '/show/ladosispositiva') {
    return {
      title: 'La Dosis Positiva | Anuncia con nosotros',
      description: 'Anuncia con nosotros en La Dosis Positiva. Bienestar, productividad y estilo de vida con Yanira Bocaranda. Opciones de patrocinio, segmentos y contenido UGC para conectar tu marca con nuestra audiencia.',
      ogImage: showLogoOgImage('ladosispositiva'),
    };
  }
  if (pathTrim === '/puntodefe' || pathTrim === '/show/puntodefe') {
    return {
      title: 'Punto de Fe | Anuncia con nosotros',
      description:
        'Anuncia en Punto de Fe con Margarita Toledo en TNO Network. Fe, valores familiares y bienestar integral. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: showLogoOgImage('puntodefe'),
    };
  }
  if (pathTrim === '/somospadres' || pathTrim === '/show/somospadres') {
    return {
      title: 'Somos Padres | Anuncia con nosotros',
      description:
        'Anuncia en Somos Padres con Yelitza Yánez en TNO Network. Crianza, salud infantil y bienestar familiar. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: showLogoOgImage('somospadres'),
    };
  }
  if (pathTrim === '/vivirenequilibrio' || pathTrim === '/show/vivirenequilibrio') {
    return {
      title: 'Vivir en Equilibrio | Anuncia con nosotros',
      description:
        'Anuncia en Vivir en Equilibrio con Liliana Fernandes en TNO Network. Bienestar integral, fitness y equilibrio mente-cuerpo. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: showLogoOgImage('vivirenequilibrio'),
    };
  }
  if (pathTrim === '/tnosports' || pathTrim === '/show/tnosports') {
    return {
      title: 'TNO Sports | Anuncia con nosotros',
      description:
        'Anuncia en TNO Sports con Yeraldin Marrero (+100K Instagram) en TNO Network. Agenda deportiva, fútbol, béisbol y Vinotinto. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: showLogoOgImage('tnosports'),
    };
  }
  if (pathTrim === '/siempremasmusica' || pathTrim === '/show/siempremasmusica') {
    return {
      title: 'Siempre más Música | Anuncia con nosotros',
      description:
        'Anuncia en Siempre más Música con Arlene Flores en TNO Network. Música, cultura, entrevistas y eventos. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: showLogoOgImage('siempremasmusica'),
    };
  }
  if (pathTrim === '/comerciocorporativo' || pathTrim === '/show/comerciocorporativo') {
    return {
      title: 'Comercio Corporativo | Anuncia con nosotros',
      description:
        'Anuncia en Comercio Corporativo con Darío Ascanio en TNO Network. Economía, gestión, finanzas y éxito empresarial. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: showLogoOgImage('comerciocorporativo'),
    };
  }
  if (pathTrim === '/despuesdelevento' || pathTrim === '/show/despuesdelevento') {
    return {
      title: 'Después del evento | Anuncia con nosotros',
      description:
        'Anuncia en Después del evento con Angélica Lugo y Yelitza Yánez en TNO Network. Resiliencia, historias humanas y periodismo de soluciones. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: showLogoOgImage('despuesdelevento'),
    };
  }
  if (pathTrim === '/ensintoniacontusalud' || pathTrim === '/show/ensintoniacontusalud') {
    return {
      title: 'En Sintonía con tu Salud | Anuncia con nosotros',
      description:
        'Anuncia en En Sintonía con tu Salud con la Dra. Grecia Andreina en TNO Network. Bienestar integral, prevención y salud. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: showLogoOgImage('ensintoniacontusalud'),
    };
  }
  if (pathTrim === '/2doescalon' || pathTrim === '/show/2doescalon') {
    return {
      title: '2do Escalón | Anuncia con nosotros',
      description:
        'Anuncia en 2do Escalón en TNO Network. Cultura pop, juventud y entretenimiento universitario. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: showLogoOgImage('2doescalon'),
    };
  }
  if (pathTrim === '/hablemosdetodo' || pathTrim === '/show/hablemosdetodo') {
    return {
      title: 'Hablemos de Todo | Anuncia con nosotros',
      description:
        'Anuncia en Hablemos de Todo con Janeth Cano en TNO Network. Relaciones, liderazgo, bienestar y desarrollo personal. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: showLogoOgImage('hablemosdetodo'),
    };
  }
  if (pathTrim === '/vidainmobiliaria' || pathTrim === '/show/vidainmobiliaria') {
    return {
      title: 'Vida Inmobiliaria | Anuncia con nosotros',
      description:
        'Anuncia en Vida Inmobiliaria con Carolina Hernández en TNO Network. Mercado inmobiliario, inversión, financiamiento y patrimonio. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: showLogoOgImage('vidainmobiliaria'),
    };
  }
  if (pathTrim === '/tnobusiness' || pathTrim === '/show/tnobusiness') {
    return {
      title: 'TNO Business | Media Kit',
      description:
        'TNO Business: autoridad corporativa, entrevistas a líderes y patrocinio B2B en TNO Network. Streaming, On-Demand y TNO Digest para marcas que buscan credibilidad empresarial.',
      ogImage: showLogoOgImage('tnobusiness'),
    };
  }
  if (
    host === 'marketingenmovimiento.tnonetwork.com' ||
    pathTrim === '/marketingenmovimiento' ||
    pathTrim === '/show/marketingenmovimiento'
  ) {
    return {
      title: 'Marketing en Movimiento | Anuncia con nosotros',
      description:
        'Anuncia en Marketing en Movimiento con Loyola Rosales y Luzmey Hernández en TNO Network. Marketing real, emprendimiento, digitalización y talento. Patrocinio, segmentos, entrevistas, TNO Digest, cápsulas y UGC.',
      ogImage: 'https://files.tnonetwork.com/shows/logo/marketingenmovimiento.png',
    };
  }
  if (host === 'gpm-eventos.tnonetwork.com' || pathTrim === '/gpm-eventos' || pathTrim === '/show/gpm-eventos') {
    return {
      title: 'GPM Eventos | Media Kit · TNO Network',
      description:
        'Media kit de GPM Eventos en TNO Network: micros en rotativa diaria, lower thirds en programas clave, entrevista mensual en estudio, artículos SEO en TNO Digest y logo fijo 24/7 en el portal. Inventario completo con formato, ubicación y frecuencia mensual.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (
    host === 'legalmenteenfamilia.tnonetwork.com' ||
    path === '/legalmenteenfamilia'
  ) {
    return {
      title: 'Legalmente en Familia | Anuncia con nosotros',
      description:
        'Media kit oficial: derecho de familia, alfabetización legal y autoridad de marca con Leila Davogustto Luna en TNO Network. Planes Oro, Plata y Bronce; presencia en vivo, On-Demand y redes.',
      ogImage: '/images/logos/LOGO%20legalmente.png',
    };
  }
  if (pathTrim === '/enproduccionconmem/gran-feria' || (host === 'enproduccionconmem.tnonetwork.com' && pathTrim === '/gran-feria')) {
    return {
      title: 'Distribuidora Gran Feria × En Producción con MEM | Propuesta TNO',
      description:
        'Plan mensual orientativo $200 USD: streaming (12 impactos diarios), segmento en cada emisión con María Eugenia Moreno, SEO en TNO Digest (2 artículos al mes). Agua mineral, refrescos, jugos y víveres al mayor; envíos gratis en Gran Caracas y nacional sin costo en grandes pedidos.',
      ogImage: 'https://files.tnonetwork.com/shows/logo/enproduccionconmem.png',
    };
  }
  if (pathTrim === '/enproduccionconmem/total' || (host === 'enproduccionconmem.tnonetwork.com' && pathTrim === '/total')) {
    return {
      title: 'Total Tools × En Producción con MEM | Plan de Liderazgo Operativo ($380)',
      description:
        'Plan mensual orientativo $380 USD: streaming ($120), segmento «Produciendo con Total Tools» ($100), TNO Digest SEO ($120), banners en portal TNO ($40). Herramientas eléctricas e inalámbricas — totaltoolsoficial.com.ve — con María Eugenia Moreno.',
      ogImage: TOTAL_TOOLS_LOGO_SRC,
    };
  }
  if (pathTrim === '/enproduccionconmem/rolda' || (host === 'enproduccionconmem.tnonetwork.com' && pathTrim === '/rolda')) {
    return {
      title: 'Rolda × En Producción con MEM | Propuesta TNO',
      description:
        'Plan mensual orientativo $280 USD: streaming ($100), UGC creativo ($100), banners web ($80). Autoridad en barbería y cuidado personal.',
      ogImage: 'https://files.tnonetwork.com/shows/logo/enproduccionconmem.png',
    };
  }
  if (pathTrim === '/enproduccionconmem/rolda-mem' || (host === 'enproduccionconmem.tnonetwork.com' && pathTrim === '/rolda-mem')) {
    return {
      title: 'Rolda × En Producción con MEM | El Momento Rolda ($100)',
      description:
        'Propuesta focalizada: endoso con producto en set, tip de estilo, cintillo con QR y logo de Rolda permanente en el mini site En Producción con MEM en TNO Network. $100/mes. Sin rotativa ni otros inventarios web TNO fuera de ese espacio.',
      ogImage: 'https://files.tnonetwork.com/shows/logo/enproduccionconmem.png',
    };
  }
  if (host === 'enproduccionconmem.tnonetwork.com' || pathTrim === '/enproduccionconmem') {
    return {
      title: 'En Producción con MEM | Anuncia con nosotros',
      description:
        'Media kit oficial: cultura publicitaria, marcas y medios con María Eugenia Moreno en TNO Network. Planes Oro, Plata y Bronce; streaming en vivo, On-Demand, TNO Digest y redes.',
      ogImage: 'https://files.tnonetwork.com/shows/logo/enproduccionconmem.png',
    };
  }
  if (host === 'hoet-partners.tnonetwork.com' || pathTrim === '/hoet-partners') {
    return {
      title: 'Hoet & Partners × Legalmente en Familia | Propuesta TNO',
      description:
        'Plan Oro exclusivo con Legalmente en Familia (Dra. Leila Davogustto): spot y rotativas con micros, integración de marca y guía en antena, dominio digital con cintillos, Digest semanal SEO. Total orientativo $290 USD/mes ($95+$85+$65+$45). TNO Network.',
      ogImage: 'https://ads.tnonetwork.com/images/logos/LOGO%20legalmente.png',
    };
  }
  if (host === 'colegio-abogados-caracas.tnonetwork.com' || pathTrim === '/colegio-abogados-caracas') {
    return {
      title: 'Ilustre Colegio de Abogados de Caracas × Legalmente en Familia | TNO',
      description:
        'Propuesta institucional con Legalmente en Familia (Dra. Davogustto): Radio Visual, Voz gremial, Digest (2 artículos/semana) y autoridad digital. Total orientativo $290 USD/mes ($95+$85+$65+$45). TNO Network.',
      ogImage: 'https://ads.tnonetwork.com/images/logos/LOGO%20legalmente.png',
    };
  }
  if (
    host === '3decopas.tnonetwork.com' ||
    pathTrim === '/3decopas' ||
    pathTrim === '/show/3decopas'
  ) {
    return {
      title: '3 de Copas con Zidney | Anuncia con nosotros',
      description:
        'Anuncia en 3 de Copas con Zidney en TNO Network: conversación, lifestyle y transformación. Patrocinio, segmentos temáticos, entrevistas, TNO Digest, cápsulas y UGC. Página oficial del show: tnonetwork.com/show/3decopas.',
      ogImage: 'https://files.tnonetwork.com/shows/logo/3decopas.png',
    };
  }
  if (path === '/show/franquiciasenvenezuela' || path === '/franquiciasenvenezuela') {
    return {
      title: 'Franquicias en Venezuela | Anuncia con nosotros',
      description: 'Franquicias en Venezuela: el ecosistema de referencia en TNO Network para analizar, promover y expandir modelos de negocio escalables. Con Argenis Carpio y una audiencia B2B de inversión, gerencia y emprendimiento.',
      ogImage: 'https://files.tnonetwork.com/shows/logo/franquiciasenvenezuela.png',
    };
  }
  if (host === 'lacteosbarinas.tnonetwork.com' || path === '/lacteosbarinas') {
    return {
      title: 'Propuesta Comercial | Lácteos Barinas & TNO Network',
      description: 'Propuesta comercial Lácteos Barinas y TNO Network: Sponsorship La Dosis Positiva, Plan UGC mensual y Plan Rotativa 24/7. Inversión total $480/mes.',
    };
  }
  if (host === 'bancaribe.tnonetwork.com' || path === '/bancaribe') {
    return {
      title: 'Propuesta Comercial | Bancaribe & TNO Network',
      description: 'Propuesta Bancaribe y TNO Network: Punto de Fe con Margarita Toledo, UGC Lab, Glosario del Progreso (SEO), Flash Mentions y Plan Rotativa 24/7. Inversión total $480/mes. Tecnología con propósito.',
    };
  }
  if (host === 'papaupa.tnonetwork.com' || path === '/papaupa') {
    return {
      title: 'Propuesta Multi-Programa | Papaupa × TNO Network',
      description:
        'Papaupa × TNO Network: Plan de Dominio Multi-Pantalla $300/mes. Rotativa/micros 24/7, menciones en Punto de Fe, Somos Padres y 2do Escalón, inserts, banners web y redes. «Un pedacito de cielo en cada etapa de tu vida».',
      ogImage: '/images/logos/papaupa.jpeg',
    };
  }
  if (host === 'movistar.tnonetwork.com' || path === '/movistar') {
    return {
      title: 'Propuesta Comercial | Movistar & TNO Network',
      description: 'Alianza Movistar y TNO Network para Punto de Fe. Conectividad con Propósito: el Aliado Tecnológico de la Esperanza. Cintillo de autoridad, Tecnología al Servicio del Mensaje, Historias de Conexión y más.',
      ogImage: '/images/logos/movistar.webp',
    };
  }
  if (host === 'artesco.tnonetwork.com' || path === '/artesco') {
    return {
      title: 'Propuesta Comercial | Artesco & TNO Network',
      description: 'El Arte de Plasmar tu Fe: Artesco en Punto de Fe con Margarita Toledo. Escritura terapéutica, arte como oración y creación de memorias. Aliado de Bienestar. Inversión total $500/mes.',
      ogImage: '/images/logos/artesco.webp',
    };
  }
  if (host === 'nexus-cultural.tnonetwork.com' || path === '/nexus-cultural') {
    return {
      title: 'Propuesta de Alianza | Nexus Cultural × Punto de Fe | TNO Network',
      description:
        'Cultura para el Alma: Nexus Cultural en Punto de Fe con Margarita Toledo. Conexión Nexus, «Alma en Cartelera» (agenda semanal en vivo y en TNO Digest), rotativa 12 spots/día, entrevista mensual, reels @PuntodeFe y banners en tnonetwork.com. $300/mes.',
      ogImage: '/images/nexus.webp',
    };
  }
  if (host === 'atache.tnonetwork.com' || path === '/atache') {
    return {
      title: 'Propuesta Comercial | Atache (Vital Pharma) & TNO Network',
      description: 'Belleza con Evidencia: Atache en Expedientes del Delito con Angélica Lugo. Dermocosmética científica, segmento El Expediente de la Piel y validación médica. Inversión total $950/mes.',
      ogImage: '/images/LOGOEXPEDIENTES.png',
    };
  }
  if (host === 'danimar-express.tnonetwork.com' || path === '/danimar-express') {
    return {
      title: 'Propuesta Express | Danimar x TNO Network',
      description:
        'Danimar en el ecosistema TNO Network: 12 spots/día, 4 historias semanales en streaming, 4 reels al mes, 4 artículos TNO Digest al mes siempre (orden, limpieza, bienestar), banners web. $99/mes (60% cash + 40% trade en productos). Proveedor oficial de limpieza TNO.',
      ogImage: '/images/logodanimar.jpg',
    };
  }
  if (host === 'chemical.tnonetwork.com' || path === '/chemical') {
    return {
      title: 'Propuesta Comercial | Chemical x TNO Network',
      description:
        'Chemical distribuidora: TNO Business, Franquicias en Venezuela, La Hora del Emprendedor Chemical, 4 artículos TNO Digest al mes (siempre), con CTA a WhatsApp, 12 spots diarios, presencia mensual en programas ancla y Reel tipo pitch. $400/mes. Soluciones que brillan.',
      ogImage: '/images/chemical.jpeg',
    };
  }
  if (host === 'danimar.tnonetwork.com' || path === '/danimar') {
    return {
      title: 'Propuesta Comercial | Danimar & TNO Network & Expedientes del Delito',
      description: 'Justicia Implacable contra la Suciedad: Danimar & TNO Network & Expedientes del Delito con Angélica Lugo y Santiago Gutiérrez. Estética en estudio, 3 historias semanales en streaming, micro Expediente Danimar, TNO Digest informe técnico SEO (Angélica Lugo) y reels. $300/mes.',
      ogImage: '/images/logodanimar.jpg',
    };
  }
  if (host === 'doctorcondominioradio.tnonetwork.com' || path === '/doctorcondominioradio') {
    return {
      title: 'Dr. Condominio Radio | La voz de la convivencia y el bienestar inmobiliario',
      description: 'Dr. Condominio Radio: asesoría, gestión y soluciones para la vida en comunidad. Conecta tu marca con copropietarios y juntas de condominio con el respaldo del Dr. Julio César López Galea.',
      ogImage: '/images/doctorcondominio.jpg',
    };
  }
  if (host === 'smarttraining.tnonetwork.com' || path === '/smarttraining') {
    return {
      title: 'Propuesta Estratégica | Smart Training x TNO Network',
      description: 'Inteligencia en Movimiento: propuesta estratégica para Smart Training con streaming 24/7, Vivir en Equilibrio con Liliana Fernandes, TNO Digest SEO, cápsulas On-Demand y presencia digital. Inversión mensual total: $160.',
      ogImage: '/images/smarttraining.jpg',
    };
  }
  if (host === 'merpoeste.tnonetwork.com' || path === '/merpoeste') {
    return {
      title: 'Alianza Estratégica | Merpoeste x TNO Network',
      description: 'Ecosistema de aceleración comercial 360 para Merpoeste con 20 entrevistas, 20 artículos SEO, 20 reels en colaboración, 2 coberturas mensuales y 4 micros semanales. Inversión mensual total: $300.',
      ogImage: '/images/merpoeste.jpg',
    };
  }
  if (path === '/empire-keeway') {
    return {
      title: 'Propuesta Estratégica | Empire Keeway en Comercio Corporativo | TNO Network',
      description:
        'Empire Keeway en Comercio Corporativo con Darío Ascanio: producción in situ 2 visitas/mes, 12 spots diarios en streaming en la señal de Radio Visual, 8 artículos SEO en TNO Digest y autoridad web. Plan mensual total $380.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (path === '/hyundai-venezuela') {
    return {
      title: 'Propuesta Estratégica | Hyundai Venezuela × TNO Network',
      description:
        'Hyundai Venezuela en TNO Network: Punto de Fe en Tucson, Somos Padres, TNO Business y Creative Tech (Ruta de Calma, Licencia Emocional, Atlas del Silencio, Copiloto de Fe). Plan $450.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (path === '/dhl-venezuela') {
    return {
      title: 'Propuesta Estratégica | DHL Venezuela × TNO Network',
      description:
        'DHL Venezuela en TNO Network: Expedientes del Delito, Noticias TNO, TNO Business, Comercio Corporativo, micros en rotativa 24/7 y TNO Digest (4 artículos/mes). Plan €530/mes.',
      ogImage: '/images/logos/dhl-logo.svg',
    };
  }
  if (path === '/lg-venezuela') {
    return {
      title: 'Propuesta Estratégica | LG Venezuela × TNO Network',
      description:
        'LG Venezuela en TNO Network: inbound (hub Life\'s Good, Digest SEO, UGC), Expedientes del Delito, Vivir en Equilibrio, TNO Sports, Comercio Corporativo y Noticias TNO. Plan €730/mes.',
      ogImage: '/images/logos/lg-logo.svg',
    };
  }
  if (path === '/cafe-san-domingo') {
    return {
      title: 'Propuesta Estratégica | Café San Domingo × TNO Network',
      description:
        'Café San Domingo en TNO Network: 3 de Copas, Vivir en Equilibrio, La Dosis Positiva, Entérate con Flavia (Chef Páez), Noticias TNO, rotativa 24/7 y TNO Digest (4 artículos/mes). Plan €495/mes.',
      ogImage: '/images/logos/cafe-san-domingo-logo.svg',
    };
  }
  if (path === '/alter-gold-programa') {
    return {
      title: 'Producción TNO Media Lab | Alter Gold Servicios de Salud',
      description:
        'Programa propio Alter Gold en TNO Media Lab: identidad gráfica, producción mensual, rotativa, clips redes/YouTube y 4 artículos TNO Digest. $230 USD/mes + $120 USD fase inicial.',
      ogImage: '/images/logos/alter-gold-logo.svg',
    };
  }
  if (path === '/alter-gold-publicidad') {
    return {
      title: 'Propuesta Estratégica | Alter Gold Servicios de Salud × TNO Network',
      description:
        'Alter Gold en TNO Network: En Sintonía con tu Salud, Vivir en Equilibrio, Noticias TNO, rotativa 24/7 y TNO Digest. Plan €285/mes.',
      ogImage: '/images/logos/alter-gold-logo.svg',
    };
  }
  if (path === '/grupo-express') {
    return {
      title: 'Propuesta por Intercambio | Grupo Express × TNO Network',
      description:
        'Grupo Express (@grupoexpress) × TNO Network: intercambio $250 USD/mes — pack radio (micros, banner, insert, web) + pack catering TNO.',
      ogImage: '/images/logos/grupo-express-logo.svg',
    };
  }
  if (path === '/banco-activo-metas-con-proposito') {
    return {
      title: 'Propuesta de Alianza | Metas con Propósito × Banco Activo | TNO Network',
      description:
        'Metas con Propósito (@metasconproposito) y Banco Activo: micro educativo financiero con cintillo de marca, 12 impactos diarios en streaming en la señal de Radio Visual, 8 artículos SEO en TNO Digest, On-Demand y banners. Plan mensual total $290.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (host === 'ledhome.tnonetwork.com' || path === '/ledhome') {
    return {
      title: 'Iluminación LED Home x TNO Network',
      description: 'Iluminación LED Home x TNO Network: 2 jornadas de producción in situ, paquete UGC mensual, 12 spots diarios en Radio Visual, 2 artículos SEO semanales en TNO Digest y autoridad web. Inversión mensual total: $460.',
      ogImage: '/images/led_home.jpg',
    };
  }
  if (host === 'expedientesdeldelito.tnonetwork.com' || path === '/expedientesdeldelito') {
    return {
      title: 'Expedientes del Delito | Anuncia con nosotros',
      description: 'Expedientes del Delito: análisis sobre criminalidad, justicia y seguridad en Venezuela. Con Santiago Gutiérrez y Angélica Lugo. Patrocinios y opciones de comercialización para marcas que buscan credibilidad y autoridad periodística.',
    };
  }
  if (host === 'expopetroleo.tnonetwork.com' || path === '/expopetroleo') {
    return {
      title: 'Expo Petróleo Maracaibo 2026 | Cobertura Corporativa',
      description: 'Propuesta de Cobertura Corporativa para Expo Petróleo Maracaibo 2026. TNO Business x TNO Network para expositores que buscan validación, autoridad sectorial y proyección de mercado durante 3 meses. Inversión total $420.',
      ogImage: '/images/logos/ISOTNOBUSINESS.png',
    };
  }
  if (host === 'exposaludplus.tnonetwork.com' || path === '/exposaludplus') {
    return {
      title: 'ExpoSaludPlus x TNO Network | 45 Días de Autoridad Médica Total',
      description:
        'Propuesta Relámpago para ExpoSaludPlus: cobertura dual en Expo y sede, 12 spots diarios por 45 días y contenido SEO en TNO Digest. Inversión única: $380 cash.',
      ogImage: '/images/logos/logo_white.PNG',
    };
  }
  if (path === '/indesagro') {
    return {
      title: 'Sistema Web de Control de Gastos Inteligente | INDESAGRO',
      description: 'Propuesta de aplicación web para registrar, visualizar y analizar egresos de INDESAGRO. Registro inteligente, dashboard en tiempo real, control de deuda y reportes automatizados. MVP $1,790 USD.',
    };
  }
  if (host === 'actitudplateada.tnonetwork.com' || path === '/actitudplateada') {
    return {
      title: 'TNO Network | Actitud Plateada 2026 - Cobertura 360',
      description: 'Propuesta de Cobertura 360: Actitud Plateada 2026. TNO Network x Torneo de Pickleball y Pádel 55+. Estilo de vida premium, longevidad activa y autoridad en medios. Inversión total $600.',
    };
  }
  if (host === 'eventopadel.tnonetwork.com' || path === '/eventopadel') {
    return {
      title: 'TNO Network | Evento de Pádel - Cobertura 360',
      description:
        'Propuesta de cobertura para evento de pádel. TNO Network con Yeraldine Marrero ($60/día). Planes Bronce ($140), Plata ($360) y Oro ($890) con cobertura en terreno, señal y prensa.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (pathTrim === '/wiki/pautas-exteriores') {
    return {
      title: 'Wiki TNO Network | Pautas en exteriores',
      description:
        'Manual de campo: laptop homologada, red y energía en cafés u oficinas, audio e imagen en locación, permisos del local, contingencia y checklist.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (pathTrim === '/wiki/operador-audiovisual') {
    return {
      title: 'Wiki TNO Network | Manual operador audiovisual',
      description:
        'Manual interno del operador audiovisual TNO Network: rol, alcance técnico, señales, protocolos y estándares de marca. Contenido servido desde Academy Service.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (pathTrim === '/wiki/subida-programas') {
    return {
      title: 'Wiki TNO Network | Subida de programas',
      description:
        'Guía completa: preparación del archivo, YouTube y otras plataformas, verificación y alta de episodios en Sistema TNO (sistema.tnonetwork.com).',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (pathTrim === '/wiki/publicar-noticias-tno') {
    return {
      title: 'Wiki TNO Network | Ghost → Canva → Instagram',
      description:
        'Ciclo de publicación: Ghost (tags, Sol Vargas, canónica), Canva (reels) e Instagram (feed e historias con link al digest).',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if ((host === 'wiki.tnonetwork.com' && pathTrim === '/') || pathTrim === '/wiki') {
    return {
      title: 'Wiki TNO Network | Manuales internos',
      description:
        'Índice de manuales y documentación operativa TNO Network: operador en cabina, pautas en exteriores, subida de programas, publicación de noticias TNO y nuevas guías.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (host === 'opticaberlud.tnonetwork.com' || path === '/opticaberlud') {
    return {
      title: 'Óptica Berlud × TNO Network | Comercialización 360',
      description:
        'Propuesta Óptica Berlud × TNO Network: noticiero, rotativa, TNO Digest, inserts y micros en estudio (Plata/Oro). Bronce $290 · Plata $480 · Oro $720/mes.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (
    host === 'corporacion-tisemte.tnonetwork.com' ||
    path === '/corporacion-tisemte' ||
    host === 'corporacion-tisem.tnonetwork.com' ||
    path === '/corporacion-tisem'
  ) {
    return {
      title: 'Corporación Tisemte × TNO Network | Comercialización 360',
      description:
        'TNO Network gestiona la campaña 360 de Corporación Tisemte: servicio integral de comunicación, no solo pauta. Noticiero, rotativa, Digest, inserts y TNO Business. Bronce €290 · Plata €480 · Oro €720/mes.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (host === 'canguro.tnonetwork.com' || path === '/canguro') {
    return {
      title: 'Canguro × TNO Network | Inbound Marketing 360°',
      description:
        'Propuesta Canguro × TNO Network: inbound 360° con planes Bronce, Plata y Oro. Rotativa, programas selectivos, TNO Digest, inserts y redes. Desde 218 €/mes (neto, sin IVA).',
      ogImage: '/images/logos/canguro-logo-black.svg',
    };
  }
  if (host === 'prudence.tnonetwork.com' || path === '/prudence') {
    return {
      title: 'Prudence × TNO Network | Inbound Marketing',
      description:
        'Propuesta Prudence × TNO Network: Placer Inteligente, Decisiones Seguras. Integración en 2do Escalón, TNO Digest, rotativa y redes. Planes Esencial 120 €, Desarrollo 220 € y Premium 350 €/mes (neto, sin IVA).',
      ogImage: 'https://files.tnonetwork.com/shows/logo/2doescalon.png',
    };
  }
  if (
    host === 'patrocinio-tno-business.tnonetwork.com' ||
    path === '/patrocinio-tno-business'
  ) {
    return {
      title: 'Propuesta Genérica | Patrocinio en TNO Business | TNO Network',
      description:
        'Plan mensual genérico para marcas: pauta en TNO Business (segmento patrocinado y menciones en vivo), banners en tnonetwork.com y artículos SEO en TNO Digest. Inversión referencial $300/mes.',
      ogImage: '/images/logos/logotnobusiness.png',
    };
  }
  if (host === 'pedroconlasmascotas.tnonetwork.com' || path === '/pedroconlasmascotas') {
    return {
      title: 'Pedro con las Mascotas | Anuncia con nosotros',
      description: 'Pedro con las Mascotas: el espacio de TNO Network para pet lovers y bienestar animal. Con Pedro Scutaro. Menciones en estudio, segmentos de especialistas y contenido editorial. Conecta tu marca con una audiencia que valora el cuidado y la lealtad.',
    };
  }
  if (host === 'taller.tnonetwork.com' || host === 'academy.tnonetwork.com' || path === '/tallerproduccion') {
    return {
      title: 'TNO Academy | De la Idea al Stream: El Máster en Radio Visual',
      description: 'TNO Academy y TNO Media Lab presentan el taller con Margarita Toledo y Yelitza Yánez. Domina el flujo de la comunicación moderna y conviértete en el arquitecto de tu propio show. The Content Evolution.',
    };
  }
  if (host === 'oratoria.tnonetwork.com' || path === '/oratoria') {
    return {
      title: 'Espacios TNO Academy | Oratoria Estratégica: Tu voz es tu activo más valioso',
      description: 'Domina el arte de influir con una experiencia de Oratoria Estratégica en TNO Network: práctica real con micrófonos, cámaras y feedback experto. Cupos limitados.',
      ogImage: '/images/FlaviaSanchezEstudioTNO.jpeg',
    };
  }
  if (path === '/tnocon' || host === 'tnocon.tnonetwork.com') {
    return {
      title: 'TNO CON 2026 | The Hybrid Shift · TNO Network',
      description:
        'Convocatoria TNO CON 2026: convergencia mediática híbrida en Las Mercedes, 20–22 de abril. Registro abierto para prensa y marcas; Creators Guild sesión interna TNO. Smart Ticket y tres días de impacto.',
      ogImage: '/images/logos/logo_white.PNG',
    };
  }
  if (path === '/pressstart') {
    return {
      title: 'Acreditacion de Prensa | TNOCON 2026',
      description: 'Registro oficial para medios en TNOCON "Press Start". Jornada exclusiva de prensa con keynote, line-up de talentos, media lab showcase y acreditacion digital.',
      ogImage: '/mosquita.svg',
    };
  }
  if (path === '/pressstart/presentacion' || path === '/pressstart/presentacion/') {
    return {
      title: 'TNO CON 2026 · Press Start | Presentación rueda de prensa',
      description:
        'Soporte visual Press Start: ecosistema multiplataforma, equipo TNO, roadmap Smart Tech y aliados. Martes 21 de abril, Las Mercedes, Caracas.',
      ogImage: '/images/logos/logo_white.PNG',
    };
  }
  if (path === '/business-level') {
    return {
      title: 'TNOCON 2026 | The Business Level · TNO Network',
      description:
        'TNOCON es la convención anual de TNO Network. The Business Level cierra la edición 2026: conozca la señal multiplataforma, la grilla, el Media Lab, métricas reales y networking con dirección comercial. 22 de abril, 10:00 a. m., Las Mercedes.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (path === '/business-level/presentacion' || path === '/business-level/presentacion/') {
    return {
      title: 'TNO CON 2026 · The Business Level | Presentación día 3',
      description:
        'Soporte visual Business Level: encaje de marca en streaming, On-Demand y Digest; métricas, servicios comerciales acreditados, grilla por nichos y aliados con propuesta activa. 22 de abril, Las Mercedes.',
      ogImage: '/images/logos/logo_white.PNG',
    };
  }
  if (host === 'svp.tnonetwork.com' || path === '/svp') {
    return {
      title: 'Propuesta Maestra de Alianza Estratégica | SVP x TNO Network',
      description:
        'SVP x TNO Network: La Evolución del Fuego. Propuesta de alianza para convertir el conocimiento técnico de la Sociedad Venezolana de Parrilleros en el activo digital más potente de la gastronomía nacional.',
      ogImage: '/images/logos/svp.png',
    };
  }
  if (path === '/wmautopartes') {
    return {
      title: 'Propuesta de Branding y Despegue Digital | WM Autopartes',
      description:
        'WM Autopartes: propuesta de branding, sistema visual y estandarización de ventas para Instagram, Marketplace y Mercado Libre. Inversión total: 220 USD. Tiempo de entrega: 3 a 6 semanas.',
      ogImage: '/images/wm-autopartes-og.svg',
    };
  }
  if (pathTrim === '/sortilegio') {
    return {
      title: 'TNO Media Lab | Propuesta Sortilegio — Posicionamiento de Marca',
      description:
        'Propuesta estratégica TNO Media Lab para Distribuidora de Quesos Sortilegio: posicionamiento de marca, autoridad comercial y expansión de mercado en Gran Caracas. Planes desde $350 USD/mes.',
      ogImage: '/images/tno-media-lab/logo.svg?v=2',
    };
  }
  if (path === '/creators-guild/roadmap' || path === '/creators-guild/roadmap/') {
    return {
      title: 'Creators Guild · Roadmap 2026 | TNO Network',
      description:
        'Presentación interna TNO CON: roadmap tecnológico y editorial 2026 para talento Creators Guild — pilares, hitos por trimestre y alineación con Media Lab.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (
    path === '/creators-guild' ||
    path === '/creator' ||
    path === '/producer' ||
    host === 'creators.tnonetwork.com' ||
    host === 'producers.tnonetwork.com'
  ) {
    return {
      title: 'Creators Guild · Profesionalizando el ADN TNO | TNOCON 2026',
      description:
        'Día 1 TNOCON: jornada intensiva del TNO Media Lab para talentos y creadores de la red. Agenda con ecosistema, media kits, IA, Digest y acceso al Creators Kit. 20 de abril, Las Mercedes.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (host === 'supercines.tnonetwork.com' || path === '/supercines') {
    return {
      title: 'Supercines x TNO Network | Media Kit',
      description: 'Supercines x TNO Network: una alianza 360° para amplificar la experiencia del cine en salas, streaming y contenido editorial dentro del ecosistema TNO.',
      ogImage: '/mosquita.jpg',
    };
  }
  if (host === 'daka.tnonetwork.com' || path === '/daka') {
    return {
      title: 'TNO Network x Daka | Propuesta Comercial',
      description: 'Evolución Daka: $800/mes. Streaming 24/7, En Producción con MEM, TNO Digest SEO, UGC y banner en TNO. Liderazgo tecnológico unido.',
      ogImage: '/daka.png',
    };
  }
  if (pathTrim === '/acerca') {
    return {
      title: 'Acerca de TNO Network',
      description:
        'Nuestro propósito: conectar marcas y audiencias con radio visual, On-Demand, TNO Digest y una comunidad que conversa en torno al contenido. Conoce el ecosistema TNO.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (pathTrim === '/programacion') {
    return {
      title: 'Parrilla TNO Network | Programación por nichos',
      description:
        'Grilla comercial TNO Network organizada por nichos: informativo, negocios, marketing, bienestar, entretenimiento y más. Referencia para patrocinio, product placement y On-Demand.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (pathTrim === '/paquetes') {
    return {
      title: 'Productos y paquetes comerciales | TNO Network',
      description:
        'Catálogo genérico TNO Network: micros, rotativas, menciones selectivas, TNO Digest, coberturas, UGC y paquetes integrados Esencial, Growth y Autoridad con precios referenciales.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  if (pathTrim === '/media-kits') {
    return {
      title: 'Media Kits TNO Network | Biblioteca comercial',
      description:
        'Explora los media kits de programas de TNO Network en una sola página: biblioteca comercial para navegar propuestas y landings por show.',
      ogImage: '/images/logos/logo_tnonetwork_black.png',
    };
  }
  const metaSlug = pathTrim.startsWith('/show/')
    ? pathTrim.slice(6).replace(/\/+$/, '')
    : pathTrim.replace(/^\//, '');
  if (metaSlug && !metaSlug.includes('/')) {
    const staticProposal = getStaticProposalBySlug(metaSlug);
    if (staticProposal?.metaTitle) {
      const defaultOg =
        staticProposal.type === 'program'
          ? staticProposal.ogImage || showLogoOgImage(metaSlug)
          : staticProposal.ogImage || DEFAULT_OG_IMAGE;
      return {
        title: staticProposal.metaTitle,
        description:
          staticProposal.metaDescription ||
          (staticProposal.type === 'program'
            ? `Anuncia en ${staticProposal.name || metaSlug} con TNO Network.`
            : `Propuesta comercial ${staticProposal.name || metaSlug} en TNO Network.`),
        ogImage: defaultOg,
      };
    }
  }
  return {
    title: 'TNO Network | Media Kit',
    description:
      'TNO Network: streaming en vivo, On-Demand, web y TNO Digest (editorial y SEO en tnonetwork.com). Conecta tu marca con audiencias activas en un ecosistema multiplataforma.',
    ogImage: DEFAULT_OG_IMAGE,
  };
}

function getDefaultPaletteForRoute(hostname = '', pathname = '') {
  const path = (pathname || '').toLowerCase();
  const host = (hostname || '').toLowerCase();
  const pathTrim = path.replace(/\/+$/, '') || '/';
  if (pathTrim.startsWith('/ticket/') || pathTrim === '/admin/scanner') {
    return 'blue';
  }
  if (
    host === 'creators.tnonetwork.com' ||
    host === 'producers.tnonetwork.com' ||
    path === '/creators-guild' ||
    pathTrim === '/creators-guild/roadmap' ||
    path === '/creator' ||
    path === '/producer'
  ) {
    return 'green';
  }
  if (host === 'noticiastno.tnonetwork.com') return 'pink';
  if (host === 'ladosispositiva.tnonetwork.com' || path === '/ladosispositiva') return 'orange';
  if (pathTrim === '/puntodefe' || pathTrim === '/show/puntodefe') return 'purple';
  if (pathTrim === '/somospadres' || pathTrim === '/show/somospadres') return 'pink';
  if (pathTrim === '/vivirenequilibrio' || pathTrim === '/show/vivirenequilibrio') return 'green';
  if (pathTrim === '/tnosports' || pathTrim === '/show/tnosports') return 'red';
  if (pathTrim === '/siempremasmusica' || pathTrim === '/show/siempremasmusica') return 'red';
  if (pathTrim === '/comerciocorporativo' || pathTrim === '/show/comerciocorporativo') return 'blue';
  if (pathTrim === '/despuesdelevento' || pathTrim === '/show/despuesdelevento') return 'purple';
  if (pathTrim === '/ensintoniacontusalud' || pathTrim === '/show/ensintoniacontusalud') return 'green';
  if (pathTrim === '/2doescalon' || pathTrim === '/show/2doescalon') return 'orange';
  if (pathTrim === '/hablemosdetodo' || pathTrim === '/show/hablemosdetodo') return 'red';
  if (pathTrim === '/vidainmobiliaria' || pathTrim === '/show/vidainmobiliaria') return 'blue';
  if (
    host === 'marketingenmovimiento.tnonetwork.com' ||
    pathTrim === '/marketingenmovimiento' ||
    pathTrim === '/show/marketingenmovimiento'
  ) {
    return 'blue';
  }
  if (
    host === 'legalmenteenfamilia.tnonetwork.com' ||
    path === '/legalmenteenfamilia'
  ) {
    return 'legalmente';
  }
  if (pathTrim === '/enproduccionconmem/gran-feria' || (host === 'enproduccionconmem.tnonetwork.com' && pathTrim === '/gran-feria')) {
    return 'pastel';
  }
  if (pathTrim === '/enproduccionconmem/total' || (host === 'enproduccionconmem.tnonetwork.com' && pathTrim === '/total')) {
    return 'pastel';
  }
  if (pathTrim === '/enproduccionconmem/rolda' || (host === 'enproduccionconmem.tnonetwork.com' && pathTrim === '/rolda')) {
    return 'pastel';
  }
  if (pathTrim === '/enproduccionconmem/rolda-mem' || (host === 'enproduccionconmem.tnonetwork.com' && pathTrim === '/rolda-mem')) {
    return 'pastel';
  }
  if (host === 'enproduccionconmem.tnonetwork.com' || pathTrim === '/enproduccionconmem') {
    return 'pastel';
  }
  if (host === 'hoet-partners.tnonetwork.com' || pathTrim === '/hoet-partners') {
    return 'legalmente';
  }
  if (host === 'colegio-abogados-caracas.tnonetwork.com' || pathTrim === '/colegio-abogados-caracas') {
    return 'legalmente';
  }
  if (
    host === '3decopas.tnonetwork.com' ||
    pathTrim === '/3decopas' ||
    pathTrim === '/show/3decopas'
  ) {
    return 'purple';
  }
  if (host === 'lacteosbarinas.tnonetwork.com' || path === '/lacteosbarinas') return 'blue';
  if (host === 'bancaribe.tnonetwork.com' || path === '/bancaribe') return 'blue';
  if (host === 'papaupa.tnonetwork.com' || path === '/papaupa') return 'pink';
  if (host === 'movistar.tnonetwork.com' || path === '/movistar') return 'blue';
  if (host === 'artesco.tnonetwork.com' || path === '/artesco') return 'blue';
  if (host === 'nexus-cultural.tnonetwork.com' || path === '/nexus-cultural') return 'purple';
  if (host === 'atache.tnonetwork.com' || path === '/atache') return 'red';
  if (host === 'danimar.tnonetwork.com' || path === '/danimar') return 'red';
  if (host === 'smarttraining.tnonetwork.com' || path === '/smarttraining') return 'red';
  if (host === 'merpoeste.tnonetwork.com' || path === '/merpoeste') return 'green';
  if (host === 'ledhome.tnonetwork.com' || path === '/ledhome') return 'blue';
  if (host === 'expedientesdeldelito.tnonetwork.com' || path === '/expedientesdeldelito') return 'red';
  if (host === 'expopetroleo.tnonetwork.com' || path === '/expopetroleo') return 'green';
  if (path === '/indesagro') return 'olive';
  if (host === 'actitudplateada.tnonetwork.com' || path === '/actitudplateada') return 'blue';
  if (host === 'eventopadel.tnonetwork.com' || path === '/eventopadel') return 'green';
  if (
    host === 'wiki.tnonetwork.com' ||
    path === '/wiki' ||
    path === '/wiki/operador-audiovisual' ||
    path === '/wiki/pautas-exteriores' ||
    path === '/wiki/subida-programas' ||
    path === '/wiki/publicar-noticias-tno'
  ) {
    return 'green';
  }
  if (host === 'opticaberlud.tnonetwork.com' || path === '/opticaberlud') return 'blue';
  if (host === 'canguro.tnonetwork.com' || path === '/canguro') return 'canguro';
  if (host === 'prudence.tnonetwork.com' || path === '/prudence') return 'prudence';
  if (host === 'expomascotas.tnonetwork.com' || path === '/expomascotas') return 'purple';
  if (host === 'pedroconlasmascotas.tnonetwork.com' || path === '/pedroconlasmascotas') return 'green';
  if (host === 'supercines.tnonetwork.com' || path === '/supercines') return 'blue';
  if (host === 'daka.tnonetwork.com' || path === '/daka') return 'daka';
  if (host === 'taller.tnonetwork.com' || host === 'academy.tnonetwork.com' || path === '/tallerproduccion') return 'pink';
  if (host === 'oratoria.tnonetwork.com' || path === '/oratoria') return 'pink';
  return 'blue';
}

function getDefaultThemeModeForRoute(hostname = '', pathname = '') {
  const path = (pathname || '').toLowerCase();
  const host = (hostname || '').toLowerCase();
  const pathTrim = path.replace(/\/+$/, '') || '/';
  if (pathTrim.startsWith('/ticket/') || pathTrim === '/admin/scanner') return 'dark';
  if (host === 'expedientesdeldelito.tnonetwork.com' || path === '/expedientesdeldelito') return 'dark';
  if (host === 'movistar.tnonetwork.com' || path === '/movistar') return 'light';
  if (host === 'artesco.tnonetwork.com' || path === '/artesco') return 'light';
  if (host === 'nexus-cultural.tnonetwork.com' || path === '/nexus-cultural') return 'light';
  return 'light';
}

export const useDarkMode = () => {
  const { pathname } = useLocation();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const defaultPalette = getDefaultPaletteForRoute(hostname, pathname);
  const defaultThemeForRoute = getDefaultThemeModeForRoute(hostname, pathname);

  const path = (pathname || '').toLowerCase();
  const host = (hostname || '').toLowerCase();
  const pathTrim = path.replace(/\/+$/, '') || '/';
  const isExpedientesRoute = path === '/expedientesdeldelito' || host === 'expedientesdeldelito.tnonetwork.com';
  const isTicketCredentialRoute = pathTrim.startsWith('/ticket/');
  const isScannerRoute = pathTrim === '/admin/scanner';

  const [themeMode, setTheme] = useState(defaultThemeForRoute);
  const [paletteType, setPalette] = useState(defaultPalette);
  const [mountedComponent, setMountedComponent] = useState(false);

  const setMode = (mode) => {
    window.localStorage.setItem('themeMode', mode);
    setTheme(mode);
  };

  const setThemePalette = (type) => {
    const palette = palettes.indexOf(type) === -1 ? defaultPalette : type;
    window.localStorage.setItem('themePalette', palette);
    setPalette(palette);
  };

  const themeToggler = () => {
    themeMode === 'light' ? setMode('dark') : setMode('light');
  };

  useEffect(() => {
    if (isExpedientesRoute) {
      setTheme('dark');
      setPalette('red');
    } else if (isTicketCredentialRoute || isScannerRoute) {
      setTheme('dark');
      setPalette('blue');
    } else {
      setTheme(getDefaultThemeModeForRoute(hostname, pathname));
      setPalette(getDefaultPaletteForRoute(hostname, pathname));
    }
    setMountedComponent(true);
  }, [pathname, hostname, isExpedientesRoute, isTicketCredentialRoute, isScannerRoute]);

  return [
    isExpedientesRoute || isTicketCredentialRoute || isScannerRoute ? 'dark' : themeMode,
    themeToggler,
    isExpedientesRoute ? 'red' : paletteType,
    setThemePalette,
    mountedComponent,
  ];
};

export default function WithLayout({
  component: Component,
  layout: Layout,
  ...rest
}) {
  const { pathname } = useLocation();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  React.useEffect(() => {
    const { title: pageTitle, description: pageDescription, ogImage } = getPageMeta(hostname, pathname);
    const imageSrc = ogImage || DEFAULT_OG_IMAGE;
    document.title = pageTitle;
    const metaTitle = document.querySelector('meta[property="og:title"]');
    if (metaTitle) metaTitle.setAttribute('content', pageTitle);
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) metaDescription.setAttribute('content', pageDescription);
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', pageDescription);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl && typeof window !== 'undefined') ogUrl.setAttribute('content', window.location.href);
    if (typeof window !== 'undefined') {
      const absolute = imageSrc.startsWith('http') ? imageSrc : `${window.location.origin}${imageSrc}`;
      const ensureMetaProperty = (property, content) => {
        let el = document.querySelector(`meta[property="${property}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute('property', property);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };
      const ensureMetaName = (name, content) => {
        let el = document.querySelector(`meta[name="${name}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute('name', name);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };
      ensureMetaProperty('og:title', pageTitle);
      ensureMetaProperty('og:description', pageDescription);
      ensureMetaProperty('og:image', absolute);
      ensureMetaProperty('og:image:secure_url', absolute);
      ensureMetaName('twitter:image', absolute);
      ensureMetaName('twitter:image:alt', pageTitle);
      ensureMetaName('twitter:title', pageTitle);
      ensureMetaName('twitter:description', pageDescription);
      let ogImageAlt = document.querySelector('meta[property="og:image:alt"]');
      if (!ogImageAlt) {
        ogImageAlt = document.createElement('meta');
        ogImageAlt.setAttribute('property', 'og:image:alt');
        document.head.appendChild(ogImageAlt);
      }
      ogImageAlt.setAttribute('content', pageTitle);
    }
  }, [hostname, pathname]);

  React.useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles && jssStyles.parentElement) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
    AOS.init({
      once: true,
      delay: 50,
      duration: 500,
      easing: 'ease-in-out',
    });
  }, []);

  const [
    themeMode,
    themeToggler,
    paletteType,
    setThemePalette,
    mountedComponent,
  ] = useDarkMode();

  useEffect(() => {
    AOS.refresh();
  }, [mountedComponent, themeMode, paletteType]);

  return (
    <ThemeProvider theme={getTheme(themeMode, paletteType)}>
      {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
      <CssBaseline />
      <Paper elevation={0} sx={{ position: 'relative' }}>
        <Box
          component="span"
          data-sacred-codes="media-kit"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            color: 'background.paper',
            fontSize: '14px',
            lineHeight: 1.4,
            userSelect: 'none',
            pointerEvents: 'none',
            whiteSpace: 'normal',
            zIndex: 1,
          }}
        >
          {SACRED_CODES_TEXT}
        </Box>
        <Layout
          themeMode={themeMode}
          themeToggler={themeToggler}
          paletteType={paletteType}
          setThemePalette={setThemePalette}
        >
          <Component
            themeMode={themeMode}
            paletteType={paletteType}
            {...rest}
          />
        </Layout>
      </Paper>
    </ThemeProvider>
  );
}

WithLayout.propTypes = {
  component: PropTypes.elementType.isRequired,
  layout: PropTypes.elementType.isRequired,
};
